import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, CalendarDays, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md rounded-2xl">
        {sent ? (
          <CardContent className="pt-8 pb-8 text-center space-y-4" data-testid="forgot-sent">
            <div className="size-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto">
              <CheckCircle2 strokeWidth={1.5} className="size-7 text-primary" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Check your inbox</h1>
            <p className="text-sm text-muted-foreground">
              If an account exists for <span className="font-semibold text-foreground">{email}</span>,
              we sent a password reset link. It expires in 1 hour.
            </p>
            <Button asChild variant="outline" className="rounded-xl w-full">
              <Link to="/login">
                <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
                Back to sign in
              </Link>
            </Button>
          </CardContent>
        ) : (
          <>
            <CardHeader className="space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <CalendarDays strokeWidth={1.5} className="size-7" />
                <span className="font-display font-extrabold text-2xl tracking-tight">Lumivera</span>
              </div>
              <CardTitle className="font-display text-3xl">Forgot your password?</CardTitle>
              <CardDescription>
                Enter your email and we'll send you a reset link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-xl h-11 text-base font-semibold"
                  data-testid="forgot-submit-button"
                >
                  <Mail strokeWidth={1.5} className="mr-2 size-4" />
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
                <Link
                  to="/login"
                  className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="forgot-back-link"
                >
                  <ArrowLeft strokeWidth={1.5} className="size-3.5 inline mr-1" />
                  Back to sign in
                </Link>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
